import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../../components/ui/Sidebar';
import AuthHeader from '../../components/ui/AuthHeader';
import PaymentModal from '../../components/ui/PaymentModal';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import CanvasBlock from './components/CanvasBlock';
import SuggestionModal from './components/SuggestionModal';
import CompletionIndicator from './components/CompletionIndicator';
import jsPDF from "jspdf";

const BusinessModelCanvas = () => {
  const navigate = useNavigate();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [activeSuggestionBlock, setActiveSuggestionBlock] = useState(null);
  const [isSuggestionModalOpen, setIsSuggestionModalOpen] = useState(false);
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false);
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);
  const [isLoadingCanvasData, setIsLoadingCanvasData] = useState(true);

  const [canvasData, setCanvasData] = useState([
    {
      id: 'key-partners',
      title: 'Key Partners',
      description: 'Who are your key partners and suppliers?',
      value: '',
      placeholder: 'List your strategic partners, suppliers, and key stakeholders who help your business operate...',
      position: { row: 1, col: 1 }
    },
    {
      id: 'key-activities',
      title: 'Key Activities',
      description: 'What key activities does your value proposition require?',
      value: '',
      placeholder: 'Describe the most important activities your business must perform to operate successfully...',
      position: { row: 2, col: 1 }
    },
    {
      id: 'key-resources',
      title: 'Key Resources',
      description: 'What key resources does your value proposition require?',
      value: '',
      placeholder: 'List the critical assets, infrastructure, and resources needed to deliver your value proposition...',
      position: { row: 3, col: 1 }
    },
    {
      id: 'value-proposition',
      title: 'Value Proposition',
      description: 'What value do you deliver to customers?',
      value: '',
      placeholder: 'Describe the unique value your products or services provide to solve customer problems...',
      position: { row: 1, col: 2, rowSpan: 2 }
    },
    {
      id: 'customer-relationships',
      title: 'Customer Relationships',
      description: 'What type of relationship does each customer segment expect?',
      value: '',
      placeholder: 'Explain how you acquire, retain, and grow your customer relationships...',
      position: { row: 1, col: 3 }
    },
    {
      id: 'channels',
      title: 'Channels',
      description: 'Through which channels do customers want to be reached?',
      value: '',
      placeholder: 'List the communication, distribution, and sales channels you use to reach customers...',
      position: { row: 2, col: 3 }
    },
    {
      id: 'customer-segments',
      title: 'Customer Segments',
      description: 'For whom are you creating value?',
      value: '',
      placeholder: 'Define the different groups of people or organizations you aim to reach and serve...',
      position: { row: 1, col: 4, rowSpan: 2 }
    },
    {
      id: 'cost-structure',
      title: 'Cost Structure',
      description: 'What are the most important costs in your business model?',
      value: '',
      placeholder: 'List all major costs involved in operating your business model...',
      position: { row: 3, col: 2, colSpan: 2 }
    },
    {
      id: 'revenue-streams',
      title: 'Revenue Streams',
      description: 'For what value are customers willing to pay?',
      value: '',
      placeholder: 'Describe how your business generates income from each customer segment...',
      position: { row: 3, col: 4 }
    }
  ]);

  const suggestionsByBlock = {
    'key-partners': [
      'Technology vendors providing cloud infrastructure and software solutions for scalable operations',
      'Strategic distribution partners with established networks in target markets',
      'Industry associations and regulatory bodies ensuring compliance and market access',
      'Financial institutions offering credit facilities and payment processing services'
    ],
    'key-activities': [
      'Product development and continuous innovation based on customer feedback and market trends',
      'Quality assurance and testing to maintain high standards and customer satisfaction',
      'Marketing campaigns across digital channels to build brand awareness and generate leads',
      'Customer support operations providing timely assistance and building long-term relationships'
    ],
    'key-resources': [
      'Skilled workforce with expertise in technology, sales, and customer service',
      'Proprietary technology platform and intellectual property assets',
      'Brand reputation and customer trust built through consistent quality delivery',
      'Financial capital for operations, growth initiatives, and market expansion'
    ],
    'value-proposition': [
      'Comprehensive business intelligence platform combining strategy, analytics, and goal tracking in one solution',
      'AI-powered insights that transform complex data into actionable business decisions',
      'Time-saving automation reducing manual planning work by 70% compared to traditional methods',
      'Affordable pricing making enterprise-grade tools accessible to SMEs and startups'
    ],
    'customer-relationships': [
      'Personalized onboarding process with dedicated success managers for premium customers',
      'Self-service portal with extensive documentation, tutorials, and community forums',
      'Proactive support through automated alerts, recommendations, and regular check-ins',
      'Continuous engagement via educational content, webinars, and industry best practices'
    ],
    'channels': [
      'Direct sales through company website with free trial and demo options',
      'Digital marketing via SEO, content marketing, and social media platforms',
      'Partner network including business consultants and accounting firms',
      'Email campaigns targeting specific industries with tailored messaging'
    ],
    'customer-segments': [
      'Early-stage startups seeking structured frameworks for business planning and validation',
      'Small to medium enterprises requiring data-driven decision-making tools',
      'Business consultants and advisors serving multiple clients with strategic planning needs',
      'Entrepreneurs and solopreneurs managing growth without dedicated strategy teams'
    ],
    'cost-structure': [
      'Technology infrastructure costs including cloud hosting, data storage, and AI processing',
      'Personnel expenses for development, customer support, and sales teams',
      'Marketing and customer acquisition costs across digital and traditional channels',
      'Research and development investments for product innovation and feature enhancements'
    ],
    'revenue-streams': [
      'Subscription-based pricing with tiered plans (Free, Growth, Scale, Enterprise)',
      'Premium feature unlocks for advanced analytics, unlimited reports, and priority support',
      'Professional services including custom integrations and dedicated training sessions',
      'Enterprise licensing for organizations requiring multi-user access and custom deployments'
    ]
  };

  useEffect(() => {
    const fetchCanvasData = async () => {
      try {
        setIsLoadingCanvasData(true);

        const response = await fetch('http://localhost:5000/api/business/latest');
        const result = await response.json();

        console.log("latest API response:", result);


        const apiData = result?.data || result;

        if (response.ok && apiData) {
          setCanvasData((prev) =>
            prev.map((block) => {
              switch (block.id) {
                case 'key-partners':
                  return { ...block, value: apiData.keyPartners || '' };
                case 'key-activities':
                  return { ...block, value: apiData.keyActivities || '' };
                case 'key-resources':
                  return { ...block, value: apiData.keyResources || '' };
                case 'value-proposition':
                  return { ...block, value: apiData.valueProposition || '' };
                case 'customer-relationships':
                  return { ...block, value: apiData.customerRelationships || '' };
                case 'channels':
                  return { ...block, value: apiData.channels || '' };
                case 'customer-segments':
                  return { ...block, value: apiData.customerSegments || '' };
                case 'cost-structure':
                  return { ...block, value: apiData.costStructure || '' };
                case 'revenue-streams':
                  return { ...block, value: apiData.revenueStreams || '' };
                default:
                  return block;
              }
            })
          );
        }
      } catch (error) {
        console.error('Fetch canvas data error:', error);
      } finally {
        setIsLoadingCanvasData(false);
      }
    };

    fetchCanvasData();
  }, []);


  const handleBlockChange = (blockId, value) => {
    setCanvasData((prev) => {
      const updatedBlocks = prev.map((block) =>
        block.id === blockId ? { ...block, value } : block
      );

      saveCanvasToDatabase(updatedBlocks);
      return updatedBlocks;
    });
  };

  const handleSuggest = (blockId) => {
    setActiveSuggestionBlock(blockId);
    setIsSuggestionModalOpen(true);
    setIsLoadingSuggestions(true);

    setTimeout(() => {
      setIsLoadingSuggestions(false);
    }, 1500);
  };

  const handleAcceptSuggestion = (suggestion) => {
    if (activeSuggestionBlock) {
      const currentBlock = canvasData.find((b) => b.id === activeSuggestionBlock);
      const newValue = currentBlock?.value
        ? `${currentBlock.value}\n\n${suggestion}`
        : suggestion;

      handleBlockChange(activeSuggestionBlock, newValue);
      setIsSuggestionModalOpen(false);
      setActiveSuggestionBlock(null);
    }
  };

  const handleRejectSuggestion = (index) => {
    console.log(`Rejected suggestion ${index}`);
  };

  const handleGenerateReport = () => {
    const completedBlocks = canvasData.filter(
      (block) => block.value && block.value.length > 10
    ).length;

    if (completedBlocks < canvasData.length) {
      alert("Please complete all canvas blocks before generating the strategy report.");
      return;
    }

    setIsGeneratingReport(true);

    const newReport = {
      id: Date.now(),
      createdAt: new Date().toLocaleString(),
      data: canvasData,
    };

    const existingReports = JSON.parse(localStorage.getItem("bmc_reports_history")) || [];
    const updatedReports = [newReport, ...existingReports];

    localStorage.setItem("bmc_reports_history", JSON.stringify(updatedReports));
    localStorage.setItem("bmc_report", JSON.stringify(canvasData));

    setTimeout(() => {
      setIsGeneratingReport(false);
      navigate("/reports");
    }, 800);
  };
  // const handleDownloadPDF = () => {
  //   setIsPaymentModalOpen(true);
  // };

  const handleDownloadPDF = () => {
    const data = JSON.parse(localStorage.getItem("bmc_report"));

    if (!data || data.length === 0) {
      alert("Please generate report first");
      return;
    }

    const doc = new jsPDF();

    let y = 10;

    doc.setFontSize(18);
    doc.text("Business Model Canvas Report", 10, y);

    y += 10;

    doc.setFontSize(12);

    data.forEach((block) => {
      if (y > 270) {
        doc.addPage();
        y = 10;
      }

      doc.setFont(undefined, "bold");
      doc.text(block.title, 10, y);
      y += 6;

      doc.setFont(undefined, "normal");

      const lines = doc.splitTextToSize(block.value || "No data", 180);
      doc.text(lines, 10, y);

      y += lines.length * 6 + 4;
    });

    doc.save("business-model-canvas.pdf");
  };
  // ---------------------------

  const handlePaymentSuccess = () => {
    alert('Payment successful! Your Business Model Canvas PDF is being prepared for download.');
  };

  const completedBlocks = canvasData.filter(
    (block) => block.value && block.value.length > 50
  ).length;

  const isCanvasComplete = completedBlocks === canvasData.length;

  const saveCanvasToDatabase = async (updatedBlocks) => {
    try {
      const payload = {
        keyPartners: updatedBlocks.find((b) => b.id === 'key-partners')?.value || '',
        keyActivities: updatedBlocks.find((b) => b.id === 'key-activities')?.value || '',
        keyResources: updatedBlocks.find((b) => b.id === 'key-resources')?.value || '',
        valueProposition: updatedBlocks.find((b) => b.id === 'value-proposition')?.value || '',
        customerRelationships: updatedBlocks.find((b) => b.id === 'customer-relationships')?.value || '',
        channels: updatedBlocks.find((b) => b.id === 'channels')?.value || '',
        customerSegments: updatedBlocks.find((b) => b.id === 'customer-segments')?.value || '',
        costStructure: updatedBlocks.find((b) => b.id === 'cost-structure')?.value || '',
        revenueStreams: updatedBlocks.find((b) => b.id === 'revenue-streams')?.value || '',
      };

      const response = await fetch('http://localhost:5000/api/business/update-latest', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const result = await response.json();
      console.log('Canvas update result:', result);
    } catch (error) {
      console.error('Canvas save error:', error);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <AuthHeader isAuthenticated userName="Business Owner" userSubscription="free" />
      <Sidebar isCollapsed={isSidebarCollapsed} userSubscription="free" />

      <main className={`pt-20 transition-smooth ${isSidebarCollapsed ? 'lg:pl-20' : 'lg:pl-60'}`}>
        <div className="max-w-[1600px] mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8">
          <div className="mb-6 md:mb-8">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
              <div>
                <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground mb-2">
                  Business Model Canvas
                </h1>
                <p className="text-sm md:text-base text-muted-foreground font-body">
                  Develop and refine your business strategy with AI-powered insights
                </p>
              </div>

              <div className="flex items-center gap-3">
                <Button
                  variant="outline"
                  size="default"
                  onClick={handleDownloadPDF}
                  iconName="Download"
                  iconPosition="left"
                  className="hidden sm:inline-flex"
                >
                  Download PDF
                </Button>

                <Button
                  variant="default"
                  size="default"
                  onClick={handleGenerateReport}
                  loading={isGeneratingReport}
                  disabled={!isCanvasComplete}
                  iconName="FileText"
                  iconPosition="left"
                >
                  Generate Report
                </Button>
              </div>
            </div>

            <CompletionIndicator blocks={canvasData} />
          </div>

          {isLoadingCanvasData ? (
            <div className="bg-card border border-border rounded-lg p-10 text-center">
              <div className="w-12 h-12 border-4 border-primary/30 border-t-primary rounded-full animate-spin mx-auto mb-4" />
              <p className="text-sm text-muted-foreground font-body">
                Loading canvas data...
              </p>
            </div>
          ) : (
            <>
              <div className="hidden lg:grid grid-cols-4 gap-4 mb-6">
                {canvasData.map((block) => (
                  <div
                    key={block.id}
                    style={{
                      gridColumn: `${block.position.col} / span ${block.position.colSpan || 1}`,
                      gridRow: `${block.position.row} / span ${block.position.rowSpan || 1}`
                    }}
                    className="min-h-[280px]"
                  >
                    <CanvasBlock
                      title={block.title}
                      description={block.description}
                      value={block.value}
                      onChange={(value) => handleBlockChange(block.id, value)}
                      onSuggest={() => handleSuggest(block.id)}
                      placeholder={block.placeholder}
                      isLoading={isLoadingSuggestions && activeSuggestionBlock === block.id}
                    />
                  </div>
                ))}
              </div>

              <div className="lg:hidden space-y-4">
                {canvasData.map((block) => (
                  <CanvasBlock
                    key={block.id}
                    title={block.title}
                    description={block.description}
                    value={block.value}
                    onChange={(value) => handleBlockChange(block.id, value)}
                    onSuggest={() => handleSuggest(block.id)}
                    placeholder={block.placeholder}
                    isLoading={isLoadingSuggestions && activeSuggestionBlock === block.id}
                  />
                ))}
              </div>
            </>
          )}

          <div className="mt-6 p-4 md:p-6 bg-primary/10 border border-primary/20 rounded-lg">
            <div className="flex items-start gap-3 md:gap-4">
              <div className="flex-shrink-0 w-10 h-10 md:w-12 md:h-12 rounded-lg bg-primary/20 flex items-center justify-center">
                <Icon name="Info" size={20} color="var(--color-primary)" />
              </div>

              <div className="flex-1">
                <h3 className="text-base md:text-lg font-heading font-semibold text-foreground mb-2">
                  How to Use the Business Model Canvas
                </h3>

                <ul className="space-y-2 text-sm text-muted-foreground font-body">
                  <li className="flex items-start gap-2">
                    <Icon name="Check" size={16} color="var(--color-primary)" className="flex-shrink-0 mt-0.5" />
                    <span>Fill in each block with detailed information about your business model</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Icon name="Check" size={16} color="var(--color-primary)" className="flex-shrink-0 mt-0.5" />
                    <span>Use the "Suggest Ideas" button to get AI-powered recommendations based on your industry</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Icon name="Check" size={16} color="var(--color-primary)" className="flex-shrink-0 mt-0.5" />
                    <span>Complete all blocks to unlock the strategy report generation feature</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Icon name="Check" size={16} color="var(--color-primary)" className="flex-shrink-0 mt-0.5" />
                    <span>Download your completed canvas as a professional PDF document</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <div className="sm:hidden mt-6">
            <Button
              variant="outline"
              size="lg"
              onClick={handleDownloadPDF}
              iconName="Download"
              iconPosition="left"
              fullWidth
            >
              Download PDF
            </Button>
          </div>
        </div>
      </main>

      <SuggestionModal
        isOpen={isSuggestionModalOpen}
        onClose={() => {
          setIsSuggestionModalOpen(false);
          setActiveSuggestionBlock(null);
        }}
        blockTitle={canvasData.find((b) => b.id === activeSuggestionBlock)?.title || ''}
        suggestions={activeSuggestionBlock ? suggestionsByBlock[activeSuggestionBlock] : []}
        onAccept={handleAcceptSuggestion}
        onReject={handleRejectSuggestion}
        isLoading={isLoadingSuggestions}
      />

      {/* this line payment popup */}
      {/* <PaymentModal
        isOpen={isPaymentModalOpen}
        onClose={() => setIsPaymentModalOpen(false)}
        featureName="Business Model Canvas PDF Export"
        featureDescription="Download your complete Business Model Canvas as a professionally formatted PDF document"
        onSuccess={handlePaymentSuccess}
      /> */}
    </div>
  );
};

export default BusinessModelCanvas;